/**
 * AgentModePrompt — Prompt de ativação do Modo Agente
 *
 * Exibido quando a Megan detecta que uma tarefa precisa do sandbox.
 * O usuário pode aceitar ou recusar a ativação.
 *
 * Adaptado ao design system do projeto (bg-card, text-foreground, etc.)
 *
 * @module components/sandbox/AgentModePrompt
 */
import {
  Monitor,
  Terminal,
  Globe,
  Shield,
  Zap,
  X,
  Loader2,
} from "lucide-react";

interface AgentModePromptProps {
  onAccept: () => void;
  onDecline: () => void;
  isLoading: boolean;
  reason?: string;
}

export function AgentModePrompt({
  onAccept,
  onDecline,
  isLoading,
  reason,
}: AgentModePromptProps) {
  return (
    <div className="mx-auto max-w-lg animate-in fade-in slide-in-from-bottom-4 duration-300">
      <div className="bg-card border border-primary/20 rounded-xl p-5 shadow-lg shadow-primary/5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Monitor className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-foreground">
                Ativar Modo Agente?
              </h3>
              <p className="text-xs text-muted-foreground">
                A Megan precisa de um ambiente completo
              </p>
            </div>
          </div>
          <button
            onClick={onDecline}
            className="p-1 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Razão */}
        {reason && (
          <div className="bg-secondary/50 rounded-lg p-3 mb-4 text-xs text-muted-foreground">
            <Zap className="w-3.5 h-3.5 inline-block mr-1.5 text-yellow-500" />
            {reason}
          </div>
        )}

        {/* Capabilities */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="flex flex-col items-center gap-1.5 p-2.5 bg-secondary/50 rounded-lg">
            <Terminal className="w-4 h-4 text-green-400" />
            <span className="text-[10px] text-muted-foreground text-center">
              Terminal Linux
            </span>
          </div>
          <div className="flex flex-col items-center gap-1.5 p-2.5 bg-secondary/50 rounded-lg">
            <Globe className="w-4 h-4 text-blue-400" />
            <span className="text-[10px] text-muted-foreground text-center">
              Browser Web
            </span>
          </div>
          <div className="flex flex-col items-center gap-1.5 p-2.5 bg-secondary/50 rounded-lg">
            <Shield className="w-4 h-4 text-yellow-500" />
            <span className="text-[10px] text-muted-foreground text-center">
              Sandbox Isolado
            </span>
          </div>
        </div>

        {/* Info */}
        <p className="text-[11px] text-muted-foreground/70 mb-4 leading-relaxed">
          O Modo Agente provisiona um ambiente Linux isolado com acesso a
          internet. Você poderá acompanhar o browser e terminal em tempo real.
          O sandbox é automaticamente encerrado após a conclusão da tarefa.
        </p>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={onAccept}
            disabled={isLoading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Provisionando...
              </>
            ) : (
              <>
                <Zap className="w-3.5 h-3.5" />
                Ativar Modo Agente
              </>
            )}
          </button>
          <button
            onClick={onDecline}
            disabled={isLoading}
            className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-50 transition-all"
          >
            Não agora
          </button>
        </div>
      </div>
    </div>
  );
}
